/**
 * MilestoneDetailDialog — Reusable detail screen for a single roadmap
 * milestone (used by both `RoadmapSection` and `ReconstructionRoadmap`
 * at the bottom of the public landing page).
 *
 * Props:
 *   open       : boolean — whether the dialog is open
 *   onClose    : () => void
 *   milestone  : {
 *     name              — required, headline title
 *     description       — long-form copy (HTML stripped, just text)
 *     start_date / end_date — ISO yyyy-mm-dd (optional)
 *     status            — 'done' | 'active' | 'upcoming' | (custom)
 *     accent            — CSS color for the banner + status pill
 *     progress_percent  — 0..100 (optional)
 *     events_done       — number of tasks complete (optional)
 *     events_total      — number of tasks total (optional)
 *     parent            — { name, accent } when this milestone is a TASK
 *                         inside a parent PHASE (for ReconstructionRoadmap)
 *     children          — [{ title, description, status, progress_percent, due_date }]
 *                         optional list of child events for the in-dialog
 *                         "Linked tasks" section
 *     kicker            — small label above the title (e.g. "Phase 1" or
 *                         "Construction & Tech Integration")
 *   }
 *
 * Behaviour:
 *   - Renders inside a Radix Dialog (already mounted globally via the
 *     Toaster). Esc to close, click-outside to close.
 *   - Header carries a coloured band matching `milestone.accent` so the
 *     dialog feels visually anchored to the phase the user clicked.
 *   - Progress bar appears only when `progress_percent` is set.
 *   - "Print summary" prints just this milestone via /lib/print.js.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar, CheckCircle2, Circle, Hourglass, Printer, Layers } from 'lucide-react';
import { printHtml } from '../lib/print';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  } catch { return iso; }
}

const STATUS_META = {
  done:     { label: 'Completed',   bg: '#22C55E', Icon: CheckCircle2 },
  active:   { label: 'In progress', bg: '#EAB308', Icon: Circle },
  upcoming: { label: 'Upcoming',    bg: '#A3D4D4', Icon: Hourglass },
};

export default function MilestoneDetailDialog({ open, onClose, milestone }) {
  if (!milestone) return null;
  const accent = milestone.accent || '#003DA5';
  const status = STATUS_META[milestone.status] || null;
  const pct = milestone.progress_percent;
  const showProgress = pct !== undefined && pct !== null && Number.isFinite(pct);

  const handlePrint = () => {
    const meta = [];
    if (milestone.kicker) meta.push(`<p style="font-size:11px;letter-spacing:0.18em;color:#003DA5;font-weight:bold;text-transform:uppercase;margin:0 0 6px">${milestone.kicker}</p>`);
    if (milestone.start_date || milestone.end_date) {
      meta.push(`<p style="font-size:12px;color:#4A5D5D;margin:0 0 8px"><b>Timeline:</b> ${fmtDate(milestone.start_date)} → ${fmtDate(milestone.end_date)}</p>`);
    }
    if (status) meta.push(`<p style="font-size:12px;color:#4A5D5D;margin:0 0 8px"><b>Status:</b> ${status.label}${showProgress ? ` · ${pct}% complete` : ''}</p>`);
    if (milestone.parent?.name) meta.push(`<p style="font-size:12px;color:#4A5D5D;margin:0 0 8px"><b>Phase:</b> ${milestone.parent.name}</p>`);
    const bodyHtml = `
      <div style="border-top:6px solid ${accent};padding-top:16px;margin-bottom:20px"></div>
      <h1 style="font-family:'Cormorant Garamond',serif;font-size:34px;color:#1A2E2E;margin:0 0 12px">${milestone.name}</h1>
      ${meta.join('')}
      <h2 style="font-size:14px;text-transform:uppercase;letter-spacing:0.15em;color:#003DA5;margin:24px 0 8px">Description</h2>
      <p style="font-size:14px;color:#1A2E2E;line-height:1.6;white-space:pre-wrap">${milestone.description || 'No description provided.'}</p>
      ${(milestone.children || []).length ? `
        <h2 style="font-size:14px;text-transform:uppercase;letter-spacing:0.15em;color:#003DA5;margin:24px 0 8px">Linked tasks</h2>
        <ul style="font-size:13px;color:#1A2E2E;line-height:1.6;padding-left:18px">
          ${milestone.children.map((c) => `<li><b>${c.title}</b>${c.due_date ? ` <i>(${fmtDate(c.due_date)})</i>` : ''}${c.progress_percent != null ? ` — ${c.progress_percent}% complete` : ''}${c.description ? `<br/><span style="color:#4A5D5D">${c.description}</span>` : ''}</li>`).join('')}
        </ul>
      ` : ''}
    `;
    printHtml(bodyHtml, milestone.name);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="milestone-detail-dialog"
        className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto p-0"
        style={{ fontFamily: 'Manrope' }}
      >
        {/* Accent band */}
        <div style={{ height: 6, background: accent }} />

        <DialogHeader className="px-6 pt-5 pb-3 space-y-1">
          {(milestone.kicker || milestone.parent?.name) && (
            <p
              className="text-[10px] uppercase tracking-[0.2em] font-bold"
              style={{ color: accent, fontFamily: 'Manrope' }}
            >
              {milestone.parent?.name ? `${milestone.parent.name} · ` : ''}{milestone.kicker || ''}
            </p>
          )}
          <DialogTitle
            data-testid="milestone-detail-title"
            className="text-2xl sm:text-3xl"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E', fontWeight: 500 }}
          >
            {milestone.name}
          </DialogTitle>
          {status && (
            <DialogDescription className="text-xs flex items-center gap-2 pt-1" style={{ color: '#4A5D5D' }}>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: `${status.bg}22`, color: status.bg }}
                data-testid="milestone-detail-status"
              >
                <status.Icon className="w-3 h-3" /> {status.label}
              </span>
              {showProgress && (
                <span className="text-[11px]" style={{ color: '#7A8C8C' }}>{pct}% complete</span>
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="px-6 pb-6 space-y-5">
          {(milestone.start_date || milestone.end_date) && (
            <div className="flex items-center gap-2 text-xs" style={{ color: '#4A5D5D' }}>
              <Calendar className="w-3.5 h-3.5" style={{ color: accent }} />
              <span data-testid="milestone-detail-dates">
                <strong style={{ color: '#1A2E2E' }}>Timeline:</strong> {fmtDate(milestone.start_date)} → {fmtDate(milestone.end_date)}
              </span>
            </div>
          )}

          {showProgress && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>
                Progress
              </p>
              <div className="h-2 rounded-full" style={{ backgroundColor: '#EAE5D9' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: accent }}
                  data-testid="milestone-detail-progress"
                />
              </div>
              {milestone.events_total !== undefined && milestone.events_total > 0 && (
                <p className="text-[10px] mt-1" style={{ color: '#7A8C8C' }}>
                  {milestone.events_done || 0}/{milestone.events_total} linked tasks · {pct}% average
                </p>
              )}
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#003DA5' }}>
              Description
            </p>
            <p
              className="text-sm whitespace-pre-wrap leading-relaxed"
              style={{ color: '#1A2E2E' }}
              data-testid="milestone-detail-description"
            >
              {milestone.description || 'No description has been added for this milestone yet. Reach out to the property team for the latest scope and schedule.'}
            </p>
          </div>

          {(milestone.children || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1" style={{ color: '#003DA5' }}>
                <Layers className="w-3 h-3" /> Linked tasks ({milestone.children.length})
              </p>
              <ul className="space-y-2" data-testid="milestone-detail-children">
                {milestone.children.map((c) => (
                  <li
                    key={c.id || c.title}
                    className="px-3 py-2 rounded-lg text-xs"
                    style={{ backgroundColor: '#FAF8F3', border: '1px solid #EAE5D9' }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-bold" style={{ color: '#1A2E2E' }}>{c.title}</p>
                      {c.due_date && (
                        <span className="text-[10px] flex-shrink-0" style={{ color: '#7A8C8C' }}>
                          {fmtDate(c.due_date)}
                        </span>
                      )}
                    </div>
                    {c.description && (
                      <p className="mt-1" style={{ color: '#4A5D5D' }}>{c.description}</p>
                    )}
                    {c.progress_percent != null && (
                      <p className="text-[10px] mt-1" style={{ color: '#7A8C8C' }}>
                        {c.progress_percent}% complete
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t" style={{ borderColor: '#EAE5D9' }}>
            <button
              type="button"
              onClick={handlePrint}
              data-testid="milestone-detail-print"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
              style={{ background: 'white', color: accent, border: `1px solid ${accent}40` }}
            >
              <Printer className="w-3.5 h-3.5" /> Print summary
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="milestone-detail-close"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
              style={{ background: accent }}
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
